import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import showToast from '@/components/ui/Toast';
import authService from '@/services/authService';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import {
  getBookingCalendar,
  saveSessionOpenings,
  updateAvailabilityMode,
  type CalendarData,
  type CalendarField,
  type CalendarSlot,
} from '@/services/bookingCalendarService';
import {
  addDays,
  closedFieldMessage,
  CUSTOM_LOCK_MESSAGE,
  formatDayTitle,
  formatMonthTitle,
  formatWeekRange,
  getFieldSlotStepMinutes,
  hourLabels,
  isInteractionLocked,
  isSlotHourPassed,
  monthGridDates,
  selectionKey,
  startOfMonth,
  startOfWeek,
  toDateKey,
} from '@/components/admin/booking/bookingCalendarUtils';

export type CalendarView = 'month' | 'week' | 'day';

export interface CustomDraft {
  fieldSlug: string;
  fieldName: string;
  keys: string[];
}

export interface ManualTarget {
  fieldSlug: string;
  date: string;
  start: string;
  end: string;
}

export interface DetailTarget {
  field: CalendarField;
  slot: CalendarSlot;
}

function rangeFor(view: CalendarView, cursor: Date) {
  if (view === 'day') {
    const key = toDateKey(cursor);
    return { start: key, end: key };
  }
  if (view === 'month') {
    const days = monthGridDates(cursor);
    return { start: toDateKey(days[0]), end: toDateKey(days[days.length - 1]) };
  }
  const start = startOfWeek(cursor);
  return { start: toDateKey(start), end: toDateKey(addDays(start, 6)) };
}

export function useBookingCalendar() {
  const arenaSlug = authService.getSlug() || localStorage.getItem('arenaSlug') || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const initialField = searchParams.get('field') || searchParams.get('field_slug') || null;
  const [view, setViewState] = useState<CalendarView>('week');
  const [cursor, setCursor] = useState(() => startOfWeek(new Date()));
  const [fieldSlug, setFieldSlug] = useState<string | null>(initialField);
  const [data, setData] = useState<CalendarData | null>(null);
  const [catalog, setCatalog] = useState<CalendarField[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [draft, setDraft] = useState<CustomDraft | null>(null);
  const [manual, setManual] = useState<ManualTarget | null>(null);
  const [detail, setDetail] = useState<DetailTarget | null>(null);
  const [now, setNow] = useState(() => new Date());
  const skipNextFetchKeyRef = useRef<string | null>(null);

  const locked = isInteractionLocked(draft !== null);
  const guard = useCallback(() => {
    if (!locked) return false;
    showToast.info(CUSTOM_LOCK_MESSAGE);
    return true;
  }, [locked]);

  const load = useCallback(async (nextView: CalendarView, nextCursor: Date, nextField: string | null, initial: boolean) => {
    if (!arenaSlug) return;
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const { start, end } = rangeFor(nextView, nextCursor);
      const result = await getBookingCalendar(arenaSlug, start, end, nextField);
      setData(result);
      if (nextField && result.fields.length === 1 && result.fields[0].slug !== nextField) {
        skipNextFetchKeyRef.current = `${nextView}|${start}|${end}|${result.fields[0].slug}`;
        setFieldSlug(result.fields[0].slug);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('field', result.fields[0].slug);
          return next;
        }, { replace: true });
      } else if (!nextField && result.fields.length === 1) {
        // Hanya punya 1 lapangan aktif: otomatis pilih lapangan tersebut
        const onlySlug = result.fields[0].slug;
        skipNextFetchKeyRef.current = `${nextView}|${start}|${end}|${onlySlug}`;
        setFieldSlug(onlySlug);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('field', onlySlug);
          return next;
        }, { replace: true });
      }
      setCatalog((current) => {
        if (!nextField) return result.fields;
        if (current.length === 0) {
          // Ambil semua lapangan di latar belakang agar pilihan filter lapangan tetap lengkap
          void getBookingCalendar(arenaSlug, start, end, null)
            .then((all) => setCatalog(all.fields))
            .catch(() => {});
          return result.fields;
        }
        return current.map((field) => {
          const fresh = result.fields.find((item) => item.slug === field.slug);
          return fresh ? { ...field, availability_mode: fresh.availability_mode, slots: fresh.slots } : field;
        });
      });
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Kalender booking gagal dimuat'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [arenaSlug, setSearchParams]);

  useEffect(() => {
    const current = searchParams.get('field') || searchParams.get('field_slug') || null;
    if (current !== fieldSlug) {
      if (!current && catalog.length === 1) {
        setFieldSlug(catalog[0].slug);
        return;
      }
      setFieldSlug(current);
    }
  }, [searchParams, catalog]);

  useEffect(() => {
    const { start, end } = rangeFor(view, cursor);
    const fetchKey = `${view}|${start}|${end}|${fieldSlug ?? ''}`;
    if (skipNextFetchKeyRef.current === fetchKey) {
      skipNextFetchKeyRef.current = null;
      return;
    }
    void load(view, cursor, fieldSlug, data === null);
  }, [view, cursor, fieldSlug, load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const fields = data?.fields ?? [];
  const isSingleFieldView = fields.length === 1;
  const slotStepMinutes = isSingleFieldView ? getFieldSlotStepMinutes(fields[0].slots) : 60;
  const hours = isSingleFieldView
    ? hourLabels(data?.grid_start || '07:00', data?.grid_end || '22:00', slotStepMinutes, fields[0].slots)
    : hourLabels(data?.grid_start || '07:00', data?.grid_end || '22:00', 60);
  const weekStart = startOfWeek(cursor);
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const title = fieldSlug ? fields[0]?.name || catalog.find((field) => field.slug === fieldSlug)?.name || 'Lapangan' : 'Semua Lapangan';
  const rangeLabel = view === 'day' ? formatDayTitle(cursor) : view === 'month' ? formatMonthTitle(cursor) : formatWeekRange(weekStart);

  const shift = (direction: number) => {
    if (guard()) return;
    if (view === 'day') setCursor(addDays(cursor, direction));
    else if (view === 'week') setCursor(addDays(cursor, direction * 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + direction, 1));
  };

  const setView = (next: CalendarView) => {
    if (guard()) return;
    if (next === 'day') setCursor(new Date());
    if (next === 'week') setCursor(startOfWeek(cursor));
    if (next === 'month') setCursor(startOfMonth(cursor));
    setViewState(next);
  };

  const reload = () => load(view, cursor, fieldSlug, false);

  return {
    arenaSlug, view, cursor, fieldSlug, data, fields, catalog, hours, slotStepMinutes, weekDays, title, rangeLabel,
    loading, refreshing, draft, manual, detail, now, locked,
    shift,
    today: () => { if (!guard()) setCursor(view === 'week' ? startOfWeek(new Date()) : view === 'month' ? startOfMonth(new Date()) : new Date()); },
    setView,
    setField: (slug: string | null) => {
      if (!guard()) {
        const resolvedSlug = !slug && catalog.length === 1 ? catalog[0].slug : slug;
        setFieldSlug(resolvedSlug);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (resolvedSlug) next.set('field', resolvedSlug);
          else next.delete('field');
          return next;
        }, { replace: true });
      }
    },
    setMode: async (field: CalendarField, mode: CalendarField['availability_mode']) => {
      if (guard()) return;
      setCatalog((current) => current.map((item) => (item.slug === field.slug ? { ...item, availability_mode: mode } : item)));
      try {
        await updateAvailabilityMode(arenaSlug, field.slug, mode);
        if (mode === 'custom') {
          const currentOpenKeys = (field.slots || [])
            .filter((slot) => slot.state === 'open')
            .map((slot) => selectionKey(slot.date, slot.public_id));
          setDraft({ fieldSlug: field.slug, fieldName: field.name, keys: currentOpenKeys });
          setFieldSlug(field.slug);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('field', field.slug);
            return next;
          }, { replace: true });
        }
        await load(view, cursor, field.slug, false);
      } catch (error) {
        showToast.error(getApiErrorMessage(error, 'Mode lapangan gagal disimpan'));
      }
    },
    onSlot: (field: CalendarField, slot: CalendarSlot) => {
      if (slot.state === 'locked') {
        showToast.info(closedFieldMessage(field.name));
        return;
      }
      if (slot.state === 'blocked') {
        showToast.info(`${field.name} tidak membuka sesi pada jam ini (Custom)`);
        return;
      }
      if (draft) {
        if (draft.fieldSlug !== field.slug) {
          showToast.info(`Pengaturan Custom sedang aktif untuk ${draft.fieldName}`);
          return;
        }
        if (slot.state === 'booked') {
          showToast.info('Sesi ini sudah memiliki booking aktif');
          return;
        }
        const key = selectionKey(slot.date, slot.public_id);
        const isSelected = draft.keys.includes(key);
        setDraft({
          ...draft,
          keys: isSelected ? draft.keys.filter((item) => item !== key) : [...draft.keys, key],
        });
        return;
      }
      if (slot.state === 'booked') {
        setDetail({ field, slot });
        return;
      }
      if (slot.state === 'open') {
        if (isSlotHourPassed(slot.date, slot.end, now)) {
          showToast.info('Sesi ini telah terlewat dan tidak dapat dibooking');
          return;
        }
        setManual({ fieldSlug: field.slug, date: slot.date, start: slot.start, end: slot.end });
      }
    },
    openManual: () => {
      if (guard()) return;
      const field = catalog[0] || fields[0];
      const today = toDateKey(now);
      const candidate = field?.slots.find((item) => item.date === today && item.state === 'open' && !isSlotHourPassed(today, item.end, now))
        || field?.slots.find((item) => item.state === 'open' && !isSlotHourPassed(item.date, item.end, now))
        || field?.slots.find((item) => item.date === today)
        || field?.slots[0];
      if (!field || !candidate) {
        showToast.info('Belum ada sesi yang bisa dibooking');
        return;
      }
      setManual({ fieldSlug: field.slug, date: candidate.date || today, start: candidate.start, end: candidate.end });
    },
    closeManual: () => setManual(null),
    closeDetail: () => setDetail(null),
    cancelDraft: () => setDraft(null),
    saveDraft: async () => {
      if (!draft) return;
      const source = fields.find((field) => field.slug === draft.fieldSlug) || catalog.find((field) => field.slug === draft.fieldSlug);
      const selectedKeySet = new Set(draft.keys);
      const openings = (source?.slots ?? [])
        .filter((slot) => selectedKeySet.has(selectionKey(slot.date, slot.public_id)))
        .map((slot) => ({ date: slot.date, time_slot_public_id: slot.public_id }));
      try {
        await saveSessionOpenings(arenaSlug, draft.fieldSlug, openings);
        showToast.success(openings.length > 0 ? `${openings.length} sesi lapangan berhasil dibuka` : 'Pengaturan sesi custom disimpan');
        setDraft(null);
        await load(view, cursor, fieldSlug, false);
      } catch (error) {
        showToast.error(getApiErrorMessage(error, 'Sesi lapangan gagal disimpan'));
      }
    },
    reload,
    focusDay: (date: Date) => {
      if (guard()) return;
      setViewState('day');
      setCursor(date);
    },
    selectedKeys: new Set(draft?.keys ?? []),
  };
}
